import React, { useState, useMemo, useEffect } from "react";
import {
  Calendar,
  Clock,
  FileText,
  History,
  Mail,
  Package,
  PawPrint,
  Phone,
  Tag,
  User,
  UserCheck,
  UserPlus,
  RefreshCw,
  CheckCircle2,
  Loader2,
  X,
  Bell,
  MapPin,
} from "lucide-react";

import { useUpdateBookingStatusMutation } from "../../../redux/actions/Admin/ServiceAdmin";

/* =========================================================
   BOOKING ENUMS
========================================================= */

export enum BookingStatus {
  Pending = 1,
  Confirmed = 2,
  InProgress = 3,
  Completed = 4,
  Cancelled = 5,
  Rescheduled = 6,
}

export enum PaymentStatus {
  Pending = 1,
  Paid = 2,
  Failed = 3,
  Refunded = 4,
  PartiallyRefunded = 5,
  Expired = 6,
}

export enum PaymentMethod {
  Unknown = 0,
  Cash = 1,
  Card = 2,
  Online = 3,
  Wallet = 4,
}

/* =========================================================
   ENUM HELPERS
========================================================= */

const getBookingStatusEnum = (val: any): BookingStatus => {
  if (typeof val === "number") {
    return val;
  }

  switch (val?.toString().toLowerCase()) {
    case "confirmed":
      return BookingStatus.Confirmed;

    case "inprogress":
    case "in-progress":
      return BookingStatus.InProgress;

    case "completed":
      return BookingStatus.Completed;

    case "cancelled":
      return BookingStatus.Cancelled;

    case "rescheduled":
      return BookingStatus.Rescheduled;

    case "pending":
    default:
      return BookingStatus.Pending;
  }
};

const getPaymentStatusEnum = (val: any): PaymentStatus => {
  if (typeof val === "number") {
    return val;
  }

  switch (val?.toString().toLowerCase()) {
    case "paid":
      return PaymentStatus.Paid;

    case "failed":
      return PaymentStatus.Failed;

    case "refunded":
      return PaymentStatus.Refunded;

    case "partiallyrefunded":
    case "partially-refunded":
      return PaymentStatus.PartiallyRefunded;

    case "expired":
      return PaymentStatus.Expired;

    case "pending":
    default:
      return PaymentStatus.Pending;
  }
};

const getPaymentMethodEnum = (val: any): PaymentMethod => {
  if (typeof val === "number") {
    return val;
  }

  switch (val?.toString().toLowerCase()) {
    case "cash":
      return PaymentMethod.Cash;

    case "card":
      return PaymentMethod.Card;

    case "online":
      return PaymentMethod.Online;

    case "wallet":
      return PaymentMethod.Wallet;

    case "unknown":
    default:
      return PaymentMethod.Unknown;
  }
};

/* =========================================================
   TYPES
========================================================= */

interface AddOn {
  id: number;
  name: string;
  price: number;
  imageUrl?: string;
  description?: string;
}

interface HistoryItem {
  historyId: number;
  action: string;

  oldStatus?: string | null;
  newStatus?: string | null;

  oldBookingDate?: string | null;
  newBookingDate?: string | null;

  oldStartTime?: string | null;
  newStartTime?: string | null;

  oldEndTime?: string | null;
  newEndTime?: string | null;

  remarks?: string;
  createdOn: string;
}

/* =========================================================
   TAXI PRICING
========================================================= */

interface TaxiPricingDetails {
  baseFare?: number;
  distanceKm?: number;
  totalPrice?: number;

  additionalCharge?: number;
  additionalDistanceKm?: number;

  dubaiBaseFare?: number;
  sharjahBaseFare?: number;
  abuDhabiBaseFare?: number;

  includedKm?: number;
  extraKmRate?: number;
  freeWaitingMinutes?: number;
}

/* =========================================================
   LOCATION
========================================================= */

interface TaxiLocation {
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
}

/* =========================================================
   SERVICE DATA
========================================================= */

interface ParsedServiceData {
  pickup?: TaxiLocation;
  drop?: TaxiLocation;

  pricing?: TaxiPricingDetails;

  totalPrice?: number;

  selectedAddOns?: AddOn[];
}

/* =========================================================
   BOOKING DETAILS
========================================================= */

interface BookingDetailsData {
  bookingId: number;
  bookingNumber: string;

  status: string;

  paymentStatus: string | number;
  paymentMethod: string | number;

  finalPrice: number;

  serviceId: number;
  serviceType?: string;
  serviceName: string;

  servicePackageId?: number | null;
  packageName: string | null;
  pricingLabel: string | null;

  userId: number;

  adminId?: number | null;

  customerName: string;
  email?: string | null;
  phoneNumber?: string | null;

  petProfileId: number;
  petName: string;
  petType: string;
  petBreed: string;
  petSize: string;

  bookingDate: string;
  startTime: string;
  endTime: string;

  durationMinutes: number;

  notes?: string | null;

  assignedEmployeeId?: number | null;
  employeeName?: string | null;

  serviceDataJson?: string | null;

  history?: HistoryItem[];
}

/* =========================================================
   PROPS
========================================================= */

interface ServiceManagementModalProps {
  isOpen: boolean;
  service: any;
  onClose: () => void;

  onAssignEmployee?: (booking: any) => void;
  onReschedule?: (booking: any) => void;
  onUpdateDuration?: (booking: any) => void;
}

/* =========================================================
   COMPONENT
========================================================= */

const ServiceManagementModal: React.FC<
  ServiceManagementModalProps
> = ({
  isOpen,
  service,
  onClose,
  onAssignEmployee,
  onReschedule,
  onUpdateDuration,
}) => {
  const [
    updateBookingStatus,
    { isLoading: isUpdatingStatus },
  ] = useUpdateBookingStatusMutation();

  const booking: BookingDetailsData | null =
    service?.data || service || null;

  /* =======================================================
     STATUS FORM
  ====================================================== */

  const [statusForm, setStatusForm] = useState({
    status: BookingStatus.Pending,
    paymentStatus: PaymentStatus.Pending,
    paymentMethod: PaymentMethod.Cash,
    remarks: "",
    notifyCustomer: true,
  });

  const [isEditingStatus, setIsEditingStatus] =
    useState(false);

  /* =======================================================
     ADMIN / STAFF ASSIGNMENT CHECK
  ====================================================== */

  const assignedStaffId =
    booking?.assignedEmployeeId ??
    booking?.adminId ??
    null;

  const isStaffAssigned =
    assignedStaffId !== null &&
    assignedStaffId !== undefined;

  /*
   * Keep this separately because the existing payment
   * controls are based on adminId.
   */
  const isAdminAssigned =
    booking?.adminId !== null &&
    booking?.adminId !== undefined;

  /* =======================================================
     SYNC FORM
  ====================================================== */

  useEffect(() => {
    if (booking) {
      setStatusForm({
        status: getBookingStatusEnum(
          booking.status
        ),

        paymentStatus: getPaymentStatusEnum(
          booking.paymentStatus
        ),

        paymentMethod: getPaymentMethodEnum(
          booking.paymentMethod
        ),

        remarks: "",

        notifyCustomer: true,
      });

      setIsEditingStatus(false);
    }
  }, [booking]);

  /* =======================================================
     PARSE SERVICE DATA
  ====================================================== */

  const parsedServiceData: ParsedServiceData | null =
    useMemo(() => {
      if (!booking?.serviceDataJson) {
        return null;
      }

      try {
        return JSON.parse(
          booking.serviceDataJson
        );
      } catch (error) {
        console.error(
          "Failed to parse serviceDataJson:",
          error
        );

        return null;
      }
    }, [booking?.serviceDataJson]);

  /* =======================================================
     TAXI PRICING
  ====================================================== */

  const taxiPricing =
    parsedServiceData?.pricing || null;

  /* =======================================================
     ADDONS
  ====================================================== */

  const parsedAddOns: AddOn[] = useMemo(() => {
    if (!booking?.serviceDataJson) {
      return [];
    }

    try {
      const parsed = JSON.parse(
        booking.serviceDataJson
      );

      return parsed?.selectedAddOns || [];
    } catch (error) {
      console.error(
        "Failed to parse serviceDataJson:",
        error
      );

      return [];
    }
  }, [booking?.serviceDataJson]);

  const addOnTotal = useMemo(() => {
    return parsedAddOns.reduce(
      (total, addon) =>
        total + (Number(addon.price) || 0),
      0
    );
  }, [parsedAddOns]);

  const totalBookingPrice = useMemo(() => {
    return (
      (Number(booking?.finalPrice) || 0) +
      addOnTotal
    );
  }, [booking?.finalPrice, addOnTotal]);

  /* =======================================================
     SUBMIT STATUS
  ====================================================== */

  const handleStatusUpdateSubmit = async () => {
    try {
      await updateBookingStatus({
        bookingId: booking?.bookingId,

        status: Number(
          statusForm.status
        ),

        paymentStatus: Number(
          statusForm.paymentStatus
        ),

        paymentMethod: Number(
          statusForm.paymentMethod
        ),

        remarks: statusForm.remarks,

        notifyCustomer:
          statusForm.notifyCustomer,
      } as any).unwrap();

      alert(
        "Booking status updated successfully!"
      );

      setIsEditingStatus(false);
    } catch (error) {
      console.error(
        "Failed to update booking status:",
        error
      );

      alert(
        "Failed to update booking status."
      );
    }
  };

  /* =======================================================
     STATUS BADGE
  ====================================================== */

  const getStatusBadge = (
    statusVal: any
  ) => {
    const status =
      getBookingStatusEnum(statusVal);

    switch (status) {
      case BookingStatus.Confirmed:
        return "bg-emerald-100 text-emerald-800 border-emerald-300";

      case BookingStatus.InProgress:
        return "bg-blue-100 text-blue-800 border-blue-300";

      case BookingStatus.Completed:
        return "bg-purple-100 text-purple-800 border-purple-300";

      case BookingStatus.Cancelled:
        return "bg-rose-100 text-rose-800 border-rose-300";

      case BookingStatus.Rescheduled:
        return "bg-amber-100 text-amber-800 border-amber-300";

      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
    }
  };

  /* =======================================================
     GUARD
  ====================================================== */

  if (
    !isOpen ||
    !service ||
    !booking
  ) {
    return null;
  }

  /* =======================================================
     RENDER
  ====================================================== */

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">

      <div
        className="
          bg-white
          rounded-2xl
          max-w-3xl
          w-full
          max-h-[90vh]
          overflow-y-auto
          shadow-2xl
          border
          border-slate-100
          flex
          flex-col
        "
      >

        {/* =================================================
            MODAL HEADER
        ================================================== */}

        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/80 sticky top-0 z-10">

          <div className="min-w-0">

            <div className="flex items-center gap-2 flex-wrap">

              <h2 className="text-xl font-bold text-slate-800">
                Booking #
                {booking.bookingNumber ||
                  booking.bookingId}
              </h2>

              <span
                className={`
                  text-xs
                  px-2.5
                  py-0.5
                  rounded-full
                  font-semibold
                  border
                  ${getStatusBadge(
                    booking.status
                  )}
                `}
              >
                {booking.status}
              </span>

            </div>

            <p className="text-xs text-slate-500 mt-0.5">
              Service:{" "}
              <span className="font-medium text-slate-700">
                {booking.serviceName}
              </span>
            </p>

          </div>

          <button
            onClick={onClose}
            className="
              p-2
              text-slate-400
              hover:text-slate-600
              hover:bg-slate-200/50
              rounded-full
              transition
              cursor-pointer
              shrink-0
            "
          >
            <X className="w-5 h-5" />
          </button>

        </div>

        {/* =================================================
            MODAL CONTENT
        ================================================== */}

        <div className="p-6 space-y-6 text-sm text-slate-700">

          {/* =================================================
              STATUS CONTROL
          ================================================== */}

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">

            <div className="flex justify-between items-center border-b border-slate-200 pb-2 gap-3">

              <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />

                Booking & Payment Status Control
              </span>

              <button
                type="button"
                onClick={() =>
                  setIsEditingStatus(
                    !isEditingStatus
                  )
                }
                className="text-xs font-bold text-violet-600 hover:text-violet-800 cursor-pointer whitespace-nowrap"
              >
                {isEditingStatus
                  ? "Cancel Edit"
                  : "Change Status"}
              </button>

            </div>

            {/* =================================================
                EDIT MODE
            ================================================== */}

            {isEditingStatus ? (
              <div className="space-y-3 pt-1">

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

                  {/* BOOKING STATUS */}

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Booking Status
                    </label>

                    <select
                      className="
                        w-full
                        border
                        border-slate-300
                        rounded-lg
                        px-2.5
                        py-1.5
                        text-xs
                        bg-white
                        font-medium
                        cursor-pointer
                      "
                      value={statusForm.status}
                      onChange={(e) =>
                        setStatusForm({
                          ...statusForm,
                          status: Number(
                            e.target.value
                          ),
                        })
                      }
                    >
                      <option value={BookingStatus.Pending}>
                        Pending
                      </option>

                      <option value={BookingStatus.Confirmed}>
                        Confirmed
                      </option>

                      <option value={BookingStatus.InProgress}>
                        In Progress
                      </option>

                      <option value={BookingStatus.Completed}>
                        Completed
                      </option>

                      <option value={BookingStatus.Cancelled}>
                        Cancelled
                      </option>

                      <option value={BookingStatus.Rescheduled}>
                        Rescheduled
                      </option>
                    </select>
                  </div>

                  {/* PAYMENT STATUS */}

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Payment Status
                    </label>

                    <select
                      disabled={!isAdminAssigned}
                      className={`
                        w-full
                        border
                        rounded-lg
                        px-2.5
                        py-1.5
                        text-xs
                        font-medium
                        ${
                          isAdminAssigned
                            ? "border-slate-300 bg-white cursor-pointer"
                            : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                        }
                      `}
                      value={
                        statusForm.paymentStatus
                      }
                      onChange={(e) =>
                        setStatusForm({
                          ...statusForm,
                          paymentStatus:
                            Number(
                              e.target.value
                            ),
                        })
                      }
                    >
                      <option value={PaymentStatus.Pending}>
                        Pending
                      </option>

                      <option value={PaymentStatus.Paid}>
                        Paid
                      </option>

                      <option value={PaymentStatus.Failed}>
                        Failed
                      </option>

                      <option value={PaymentStatus.Refunded}>
                        Refunded
                      </option>

                      <option value={PaymentStatus.PartiallyRefunded}>
                        Partially Refunded
                      </option>

                      <option value={PaymentStatus.Expired}>
                        Expired
                      </option>
                    </select>
                  </div>

                  {/* PAYMENT METHOD */}

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Payment Method
                    </label>

                    <select
                      disabled={!isAdminAssigned}
                      className={`
                        w-full
                        border
                        rounded-lg
                        px-2.5
                        py-1.5
                        text-xs
                        font-medium
                        ${
                          isAdminAssigned
                            ? "border-slate-300 bg-white cursor-pointer"
                            : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                        }
                      `}
                      value={
                        statusForm.paymentMethod
                      }
                      onChange={(e) =>
                        setStatusForm({
                          ...statusForm,
                          paymentMethod:
                            Number(
                              e.target.value
                            ),
                        })
                      }
                    >
                      <option value={PaymentMethod.Unknown}>
                        Unknown
                      </option>

                      <option value={PaymentMethod.Cash}>
                        Cash
                      </option>

                      <option value={PaymentMethod.Card}>
                        Card
                      </option>

                      <option value={PaymentMethod.Online}>
                        Online
                      </option>

                      <option value={PaymentMethod.Wallet}>
                        Wallet
                      </option>
                    </select>
                  </div>

                </div>

                {/* PAYMENT INFO */}

                {!isAdminAssigned && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-amber-800 font-medium">
                      Payment Status and Payment
                      Method can be changed only
                      after an admin is assigned
                      to this booking.
                    </p>
                  </div>
                )}

                {/* REMARKS */}

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">
                    Remarks
                  </label>

                  <input
                    type="text"
                    placeholder="Status change remarks..."
                    className="
                      w-full
                      border
                      border-slate-300
                      rounded-lg
                      px-3
                      py-1.5
                      text-xs
                      bg-white
                    "
                    value={statusForm.remarks}
                    onChange={(e) =>
                      setStatusForm({
                        ...statusForm,
                        remarks:
                          e.target.value,
                      })
                    }
                  />
                </div>

                {/* NOTIFY CUSTOMER */}

                <div className="flex justify-between items-center pt-1">

                  <label className="text-xs text-slate-600 font-semibold flex items-center gap-1.5 cursor-pointer">

                    <Bell className="w-3.5 h-3.5 text-amber-500" />

                    Notify Customer

                  </label>

                  <input
                    type="checkbox"
                    checked={
                      statusForm.notifyCustomer
                    }
                    onChange={(e) =>
                      setStatusForm({
                        ...statusForm,
                        notifyCustomer:
                          e.target.checked,
                      })
                    }
                    className="w-4 h-4 cursor-pointer accent-violet-600"
                  />

                </div>

                {/* SAVE */}

                <div className="flex justify-end pt-1">

                  <button
                    type="button"
                    disabled={
                      isUpdatingStatus
                    }
                    onClick={
                      handleStatusUpdateSubmit
                    }
                    className="
                      px-4
                      py-1.5
                      bg-emerald-600
                      hover:bg-emerald-700
                      text-white
                      rounded-lg
                      text-xs
                      font-bold
                      transition
                      flex
                      items-center
                      gap-1
                      cursor-pointer
                      disabled:opacity-60
                      disabled:cursor-not-allowed
                    "
                  >
                    {isUpdatingStatus && (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    )}

                    Save Status Changes
                  </button>

                </div>

              </div>
            ) : (

              /* =================================================
                 VIEW MODE
              ================================================== */

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">

                <div>
                  <p className="text-slate-400">
                    Current Status
                  </p>

                  <p className="font-bold text-slate-800">
                    {booking.status}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400">
                    Payment Status
                  </p>

                  <p className="font-bold text-slate-800">
                    {
                      PaymentStatus[
                        getPaymentStatusEnum(
                          booking.paymentStatus
                        )
                      ]
                    }
                  </p>
                </div>

                <div>
                  <p className="text-slate-400">
                    Payment Method
                  </p>

                  <p className="font-bold text-slate-800">
                    {
                      PaymentMethod[
                        getPaymentMethodEnum(
                          booking.paymentMethod
                        )
                      ]
                    }
                  </p>
                </div>

              </div>
            )}

          </div>

          {/* =================================================
              CUSTOMER + PET
          ================================================== */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* CUSTOMER */}

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-2">

              <div className="flex items-center gap-2 font-bold text-slate-800 border-b border-slate-200 pb-2">
                <User className="w-4 h-4 text-violet-600" />

                Customer Information
              </div>

              <div className="space-y-1 text-xs">

                <p>
                  <span className="text-slate-400">
                    Name:
                  </span>{" "}
                  <span className="font-semibold text-slate-800">
                    {booking.customerName ||
                      "N/A"}
                  </span>
                </p>

                <p className="flex items-center gap-1">
                  <Mail className="w-3 h-3 text-slate-400" />

                  <span className="break-all">
                    {booking.email ||
                      "No email provided"}
                  </span>
                </p>

                <p className="flex items-center gap-1">
                  <Phone className="w-3 h-3 text-slate-400" />

                  <span>
                    {booking.phoneNumber ||
                      "No phone provided"}
                  </span>
                </p>

              </div>

            </div>

            {/* PET */}

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-2">

              <div className="flex items-center gap-2 font-bold text-slate-800 border-b border-slate-200 pb-2">
                <PawPrint className="w-4 h-4 text-violet-600" />

                Pet Details
              </div>

              <div className="space-y-1 text-xs">

                <p>
                  <span className="text-slate-400">
                    Pet Name:
                  </span>{" "}
                  <span className="font-semibold text-slate-800">
                    {booking.petName}
                  </span>
                </p>

                <p>
                  <span className="text-slate-400">
                    Type / Breed:
                  </span>{" "}
                  <span>
                    {booking.petType} (
                    {booking.petBreed})
                  </span>
                </p>

                <p>
                  <span className="text-slate-400">
                    Size:
                  </span>{" "}
                  <span className="font-medium">
                    {booking.petSize}
                  </span>
                </p>

              </div>

            </div>

          </div>

          {/* =================================================
              ROUTE LOCATIONS
          ================================================== */}

          {parsedServiceData &&
            (parsedServiceData.pickup ||
              parsedServiceData.drop) && (

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">

                <div className="flex items-center gap-2 font-bold text-slate-800 border-b border-slate-200 pb-2">

                  <MapPin className="w-4 h-4 text-emerald-600" />

                  Route Locations
                  (Pickup & Drop)

                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">

                  {/* PICKUP */}

                  {parsedServiceData.pickup && (
                    <div className="bg-white p-3 rounded-lg border border-slate-200/80 space-y-1.5 shadow-2xs min-w-0">

                      <div className="flex justify-between items-center gap-2">

                        <span className="font-bold text-emerald-700 uppercase text-[10px] tracking-wide">
                          Pickup Location
                        </span>

                        {parsedServiceData.pickup.latitude != null &&
                          parsedServiceData.pickup.longitude != null && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${parsedServiceData.pickup.latitude},${parsedServiceData.pickup.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-[10px] font-bold text-blue-600 hover:underline"
                            >
                              Open Map
                            </a>
                          )}

                      </div>

                      {parsedServiceData.pickup.addressLine2 && (
                        <p className="font-semibold text-slate-900 bg-emerald-50/50 px-2 py-0.5 rounded text-[11px] inline-block max-w-full break-words">
                          Unit / Apt:{" "}
                          {
                            parsedServiceData
                              .pickup
                              .addressLine2
                          }
                        </p>
                      )}

                      <p className="text-slate-600 leading-relaxed break-words">
                        {
                          parsedServiceData
                            .pickup.address
                        }
                      </p>

                      <p className="text-slate-400 font-medium">
                        {
                          parsedServiceData
                            .pickup.city
                        }

                        {parsedServiceData.pickup.city &&
                          parsedServiceData.pickup.country
                          ? ", "
                          : ""}

                        {
                          parsedServiceData
                            .pickup.country
                        }
                      </p>

                    </div>
                  )}

                  {/* DROP */}

                  {parsedServiceData.drop && (
                    <div className="bg-white p-3 rounded-lg border border-slate-200/80 space-y-1.5 shadow-2xs min-w-0">

                      <div className="flex justify-between items-center gap-2">

                        <span className="font-bold text-rose-600 uppercase text-[10px] tracking-wide">
                          Drop Location
                        </span>

                        {parsedServiceData.drop.latitude != null &&
                          parsedServiceData.drop.longitude != null && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${parsedServiceData.drop.latitude},${parsedServiceData.drop.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-[10px] font-bold text-blue-600 hover:underline"
                            >
                              Open Map
                            </a>
                          )}

                      </div>

                      {parsedServiceData.drop.addressLine2 && (
                        <p className="font-semibold text-slate-900 bg-rose-50/50 px-2 py-0.5 rounded text-[11px] inline-block max-w-full break-words">
                          Unit / Apt:{" "}
                          {
                            parsedServiceData
                              .drop
                              .addressLine2
                          }
                        </p>
                      )}

                      <p className="text-slate-600 leading-relaxed break-words">
                        {
                          parsedServiceData
                            .drop.address
                        }
                      </p>

                      <p className="text-slate-400 font-medium">
                        {
                          parsedServiceData
                            .drop.city
                        }

                        {parsedServiceData.drop.city &&
                          parsedServiceData.drop.country
                          ? ", "
                          : ""}

                        {
                          parsedServiceData
                            .drop.country
                        }
                      </p>

                    </div>
                  )}

                </div>

              </div>
            )}

          {/* =================================================
              TAXI PRICING
          ================================================== */}

          {taxiPricing && (
            <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-4 space-y-4">

              <div className="flex items-center justify-between gap-3 border-b border-emerald-200 pb-2">

                <div className="flex items-center gap-2 font-bold text-slate-800">

                  <Tag className="w-4 h-4 text-emerald-600" />

                  Taxi Fare Breakdown

                </div>

                <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">
                  Pricing Details
                </span>

              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">

                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">
                    Distance
                  </p>

                  <p className="text-sm font-bold text-slate-800 mt-1">
                    {Number(
                      taxiPricing.distanceKm ?? 0
                    ).toFixed(2)}{" "}
                    KM
                  </p>
                </div>

                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">
                    Base Fare
                  </p>

                  <p className="text-sm font-bold text-slate-800 mt-1">
                    AED{" "}
                    {Number(
                      taxiPricing.baseFare ?? 0
                    ).toFixed(2)}
                  </p>
                </div>

                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">
                    Included Distance
                  </p>

                  <p className="text-sm font-bold text-slate-800 mt-1">
                    {Number(
                      taxiPricing.includedKm ?? 0
                    ).toFixed(2)}{" "}
                    KM
                  </p>
                </div>

                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">
                    Extra KM Rate
                  </p>

                  <p className="text-sm font-bold text-slate-800 mt-1">
                    AED{" "}
                    {Number(
                      taxiPricing.extraKmRate ?? 0
                    ).toFixed(2)}
                  </p>
                </div>

              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">

                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500">
                    Additional Distance
                  </span>

                  <span className="font-semibold text-slate-800">
                    {Number(
                      taxiPricing.additionalDistanceKm ??
                        0
                    ).toFixed(2)}{" "}
                    KM
                  </span>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500">
                    Additional Distance Charge
                  </span>

                  <span className="font-semibold text-slate-800">
                    AED{" "}
                    {Number(
                      taxiPricing.additionalCharge ??
                        0
                    ).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500">
                    Free Waiting Time
                  </span>

                  <span className="font-semibold text-slate-800">
                    {Number(
                      taxiPricing.freeWaitingMinutes ??
                        0
                    )}{" "}
                    minutes
                  </span>
                </div>

              </div>

              {(taxiPricing.dubaiBaseFare !== undefined ||
                taxiPricing.sharjahBaseFare !== undefined ||
                taxiPricing.abuDhabiBaseFare !== undefined) && (

                <div className="border-t border-emerald-200 pt-3">

                  <p className="text-[10px] text-slate-400 uppercase font-bold mb-2">
                    Location Base Fare
                  </p>

                  <div className="grid grid-cols-3 gap-2">

                    <div className="bg-white rounded-lg border border-slate-200 p-2.5">
                      <p className="text-[10px] text-slate-400">
                        Dubai
                      </p>

                      <p className="text-xs font-bold text-slate-800">
                        AED{" "}
                        {Number(
                          taxiPricing.dubaiBaseFare ??
                            0
                        ).toFixed(2)}
                      </p>
                    </div>

                    <div className="bg-white rounded-lg border border-slate-200 p-2.5">
                      <p className="text-[10px] text-slate-400">
                        Sharjah
                      </p>

                      <p className="text-xs font-bold text-slate-800">
                        AED{" "}
                        {Number(
                          taxiPricing.sharjahBaseFare ??
                            0
                        ).toFixed(2)}
                      </p>
                    </div>

                    <div className="bg-white rounded-lg border border-slate-200 p-2.5">
                      <p className="text-[10px] text-slate-400">
                        Abu Dhabi
                      </p>

                      <p className="text-xs font-bold text-slate-800">
                        AED{" "}
                        {Number(
                          taxiPricing.abuDhabiBaseFare ??
                            0
                        ).toFixed(2)}
                      </p>
                    </div>

                  </div>

                </div>
              )}

              <div className="border-t border-emerald-200 pt-3 flex justify-between items-center gap-3">

                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-bold">
                    Calculated Taxi Fare
                  </p>

                  <p className="text-xs text-slate-500">
                    Distance + applicable charges
                  </p>
                </div>

                <p className="text-lg font-black text-emerald-700 whitespace-nowrap">
                  AED{" "}
                  {Number(
                    taxiPricing.totalPrice ??
                      booking.finalPrice ??
                      0
                  ).toFixed(2)}
                </p>

              </div>

            </div>
          )}

          {/* =================================================
              ASSIGNED STAFF
          ================================================== */}

          <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-3.5 flex justify-between items-center text-xs gap-3">

            <div className="flex items-center gap-2 min-w-0">

              <UserCheck className="w-4 h-4 text-emerald-600 shrink-0" />

              <div className="min-w-0">

          
                <p className="font-bold text-slate-800 text-xs truncate">
                  {isStaffAssigned
                    ? "Staff Assigned" 
                    : "Unassigned"}
                </p>

              </div>

            </div>

            {onAssignEmployee && (
              <button
                onClick={() => {
                  onClose();
                  onAssignEmployee(
                    booking
                  );
                }}
                className="
                  px-3
                  py-1
                  bg-white
                  border
                  border-emerald-300
                  text-emerald-800
                  hover:bg-emerald-100
                  rounded-lg
                  font-bold
                  text-[11px]
                  cursor-pointer
                  transition
                  shrink-0
                "
              >
                {isStaffAssigned
                  ? "Change Staff"
                  : "+ Assign Staff"}
              </button>
            )}

          </div>

          {/* =================================================
              PACKAGE & TIMING
          ================================================== */}

          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-2xs">

            <div className="flex items-center gap-2 font-bold text-slate-800 border-b border-slate-100 pb-2">

              <Package className="w-4 h-4 text-violet-600" />

              Package & Timing

            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">

              <div className="min-w-0">

                <p className="text-slate-400">
                  Package
                </p>

                <p className="font-bold text-slate-800 break-words">
                  {booking.packageName ||
                    "N/A"}
                </p>

                {booking.pricingLabel && (
                  <p className="text-[11px] text-slate-400 break-words">
                    Label:{" "}
                    {booking.pricingLabel}
                  </p>
                )}

              </div>

              <div>

                <p className="text-slate-400 flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-slate-400" />

                  Date
                </p>

                <p className="font-semibold text-slate-800">
                  {booking.bookingDate}
                </p>

              </div>

              <div>

                <p className="text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3 text-slate-400" />

                  Time
                </p>

                <p className="font-semibold text-slate-800">
                  {booking.startTime} -{" "}
                  {booking.endTime}
                </p>

                <p className="text-[11px] text-slate-400">
                  {booking.durationMinutes}{" "}
                  minutes
                </p>

              </div>

            </div>

          </div>

          {/* =================================================
              SELECTED ADDONS
          ================================================== */}

          {parsedAddOns.length > 0 && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-2">

              <div className="flex items-center gap-2 font-bold text-slate-800">

                <Tag className="w-4 h-4 text-violet-600" />

                Selected Add-Ons

              </div>

              <div className="divide-y divide-slate-100">

                {parsedAddOns.map(
                  (addon) => (
                    <div
                      key={addon.id}
                      className="
                        py-2
                        flex
                        items-center
                        justify-between
                        gap-3
                        text-xs
                      "
                    >

                      <div className="flex items-center gap-2 min-w-0">

                        {addon.imageUrl && (
                          <img
                            src={
                              addon.imageUrl
                            }
                            alt={
                              addon.name
                            }
                            className="
                              w-8
                              h-8
                              rounded-lg
                              object-cover
                              border
                              border-slate-200
                              shrink-0
                            "
                          />
                        )}

                        <div className="min-w-0">

                          <p className="font-semibold text-slate-800 truncate">
                            {addon.name}
                          </p>

                          {addon.description && (
                            <p className="text-[10px] text-slate-400 break-words">
                              {
                                addon.description
                              }
                            </p>
                          )}

                        </div>

                      </div>

                      <span className="font-bold text-slate-800 whitespace-nowrap">
                        AED{" "}
                        {Number(
                          addon.price
                        ).toFixed(2)}
                      </span>

                    </div>
                  )
                )}

              </div>

            </div>
          )}

          {/* =================================================
              NOTES
          ================================================== */}

          {booking.notes && (
            <div className="bg-amber-50/60 border border-amber-200/80 rounded-xl p-3.5 space-y-1">

              <div className="flex items-center gap-1.5 font-bold text-amber-900 text-xs">

                <FileText className="w-3.5 h-3.5 text-amber-600" />

                Customer Notes

              </div>

              <p className="text-xs text-amber-800 italic break-words">
                {booking.notes}
              </p>

            </div>
          )}

          {/* =================================================
              PAYMENT DETAILS
          ================================================== */}

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 flex justify-between items-center gap-4 text-xs">

            <div className="min-w-0">

              <p className="text-slate-400">
                Payment Status / Method
              </p>

              <div className="flex items-center gap-2 mt-0.5 flex-wrap">

                <span className="font-semibold text-slate-800">
                  {
                    PaymentMethod[
                      getPaymentMethodEnum(
                        booking.paymentMethod
                      )
                    ]
                  }
                </span>

                <span className="text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-medium">
                  {
                    PaymentStatus[
                      getPaymentStatusEnum(
                        booking.paymentStatus
                      )
                    ]
                  }
                </span>

              </div>

            </div>

            <div className="text-right shrink-0">

              <p className="text-slate-400">
                Total Price
              </p>

              <p className="text-lg font-bold text-slate-900">
                AED{" "}
                {Number(
                  totalBookingPrice || 0
                ).toFixed(2)}
              </p>

            </div>

          </div>

          {/* =================================================
              BOOKING HISTORY
          ================================================== */}

          {booking.history &&
            booking.history.length > 0 && (

              <div className="border-t border-slate-100 pt-4 space-y-3">

                <div className="flex items-center gap-2 font-bold text-slate-800 text-xs">

                  <History className="w-4 h-4 text-violet-600" />

                  Booking History Log

                </div>

                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">

                  {booking.history.map(
                    (item) => (
                      <div
                        key={
                          item.historyId
                        }
                        className="
                          bg-slate-50
                          p-2.5
                          rounded-lg
                          border
                          border-slate-100
                          text-xs
                          space-y-0.5
                        "
                      >

                        <div className="flex justify-between items-center gap-3 font-medium text-slate-800">

                          <span>
                            Action:{" "}
                            <span className="font-bold">
                              {
                                item.action
                              }
                            </span>
                          </span>

                          <span className="text-[10px] text-slate-400 whitespace-nowrap">
                            {new Date(
                              item.createdOn
                            ).toLocaleString()}
                          </span>

                        </div>

                        {item.remarks && (
                          <p className="text-slate-500 text-[11px] break-words">
                            {item.remarks}
                          </p>
                        )}

                      </div>
                    )
                  )}

                </div>

              </div>
            )}

        </div>

        {/* =================================================
            MODAL FOOTER
        ================================================== */}

        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex flex-wrap justify-between items-center gap-2 sticky bottom-0 z-10">

          <div className="flex flex-wrap gap-2">

            {onAssignEmployee && (
              <button
                onClick={() => {
                  onClose();
                  onAssignEmployee(
                    booking
                  );
                }}
                className="
                  px-3
                  py-1.5
                  bg-white
                  border
                  border-slate-300
                  text-slate-700
                  hover:bg-slate-100
                  rounded-lg
                  text-xs
                  font-semibold
                  flex
                  items-center
                  gap-1
                  cursor-pointer
                  transition
                  shadow-2xs
                "
              >
                <UserPlus className="w-3.5 h-3.5 text-violet-600" />

                {isStaffAssigned
                  ? "Change Staff"
                  : "Assign Staff"}
              </button>
            )}

            {onReschedule && (
              <button
                onClick={() => {
                  onClose();
                  onReschedule(
                    booking
                  );
                }}
                className="
                  px-3
                  py-1.5
                  bg-white
                  border
                  border-slate-300
                  text-slate-700
                  hover:bg-slate-100
                  rounded-lg
                  text-xs
                  font-semibold
                  flex
                  items-center
                  gap-1
                  cursor-pointer
                  transition
                  shadow-2xs
                "
              >
                <RefreshCw className="w-3.5 h-3.5 text-amber-600" />

                Reschedule
              </button>
            )}

            {onUpdateDuration && (
              <button
                onClick={() => {
                  onClose();
                  onUpdateDuration(
                    booking
                  );
                }}
                className="
                  px-3
                  py-1.5
                  bg-white
                  border
                  border-slate-300
                  text-slate-700
                  hover:bg-slate-100
                  rounded-lg
                  text-xs
                  font-semibold
                  flex
                  items-center
                  gap-1
                  cursor-pointer
                  transition
                  shadow-2xs
                "
              >
                <Clock className="w-3.5 h-3.5 text-blue-600" />

                Edit Duration
              </button>
            )}

          </div>

          <button
            onClick={onClose}
            className="
              px-5
              py-2
              bg-slate-800
              hover:bg-slate-900
              text-white
              rounded-xl
              text-xs
              font-semibold
              transition
              cursor-pointer
            "
          >
            Close
          </button>

        </div>

      </div>

    </div>
  );
};

export default ServiceManagementModal;